import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { BookMarked, Volume2, ArrowRight, Sparkles } from "lucide-react";
import { fetchCurrentWord, speakText } from "../../lib/wow";

const TINT = {
  Feelings: "#9B51E0", Kindness: "#F25F9E", "Inner Strength": "#FF8C3B",
  Friendship: "#26C0B4", "Personal Growth": "#5B9E5B", "Emotional Actions": "#3B82C4",
  "World Wisdom": "#D4A017",
};

/**
 * Compact "Kingdom Word of the Week" widget — reused on Home, Activities, dashboards, and as a
 * slim strip inside other activities. Variants: "card" (default) | "strip".
 */
export default function WordOfWeekWidget({ variant = "card", className = "" }) {
  const [w, setW] = useState(null);
  useEffect(() => { fetchCurrentWord().then((d) => setW(d?.word || null)); }, []);
  if (!w) return null;
  const tint = TINT[w.collection] || "#9B51E0";

  if (variant === "strip") {
    return (
      <Link to="/word-of-week" data-testid="wow-strip"
        className={`flex items-center gap-3 rounded-2xl bg-white/90 border-2 border-purpleK/15 px-4 py-3 shadow-sm hover:-translate-y-0.5 transition-transform ${className}`}>
        <span className="grid place-items-center h-9 w-9 rounded-xl text-white shrink-0" style={{ background: tint }}><BookMarked size={17} /></span>
        <span className="min-w-0 flex-1">
          <span className="block text-[11px] font-display font-bold uppercase tracking-wide text-ink/45">Kingdom Word of the Week</span>
          <span className="block font-display font-bold text-ink truncate">{w.word} — <span className="font-body font-normal text-ink/60">{w.kidMeaning}</span></span>
        </span>
        <ArrowRight size={18} className="text-purpleK shrink-0" />
      </Link>
    );
  }

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
      className={`rounded-[2rem] p-6 md:p-7 text-white shadow-[0_16px_40px_rgba(0,0,0,0.14)] ${className}`}
      style={{ background: `linear-gradient(135deg, ${tint}, ${tint}cc)` }} data-testid="wow-widget">
      <div className="flex items-center gap-2 text-white/85 font-display font-bold text-sm uppercase tracking-wide">
        <Sparkles size={15} /> Kingdom Word of the Week
      </div>
      <h3 className="mt-2 font-display font-bold text-3xl md:text-4xl leading-none" data-testid="wow-widget-word">{w.word}</h3>
      {w.pronunciation && <p className="mt-1 text-white/75 text-sm">{w.pronunciation}</p>}
      <p className="mt-3 text-white/95 leading-relaxed">{w.kidMeaning}</p>
      <div className="mt-5 flex flex-wrap gap-2.5">
        <Link to="/word-of-week" data-testid="wow-widget-explore"
          className="inline-flex items-center gap-2 rounded-full bg-white font-display font-bold px-5 py-2.5 shadow hover:-translate-y-0.5 transition-transform" style={{ color: tint }}>
          Explore this word <ArrowRight size={16} />
        </Link>
        <button onClick={() => speakText(w.narrationText || `${w.word}. ${w.kidMeaning}`)} data-testid="wow-widget-listen"
          className="inline-flex items-center gap-2 rounded-full bg-white/20 text-white font-display font-semibold px-4 py-2.5 hover:bg-white/30 transition-colors">
          <Volume2 size={16} /> Listen
        </button>
      </div>
    </motion.div>
  );
}
